/***
 * ADM-FILTERS
 * Get list of customer filters and edit them
 */

import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import { ReactNode, useEffect, useState } from "react"
import { faEye, faTrash } from "@fortawesome/free-solid-svg-icons"
import { store } from "@/index"
import Space from "@/components/space"
import ListItem from "@/components/list-item"
import PageLoader from "@/components/page-loader"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVED, STATUS_SAVE_ERROR, STATUS_SAVING } from "@/redux/_status.types"
import { Filter, FilterState } from "@/redux/filter.types"
import {
  filterActivate,
  filterAdd,
  filterDestroy,
  filterEditAndUpdate,
  filterFetch,
  filterGet,
  filterRemove,
  filterStatus
} from "@/redux/filter.actions"
import ModalConfirm from "@/components/modal-confirm"
import Card from "@/components/card"
import ListFields from "@/components/list-fields"
import ListCell from "@/components/list-cell"
import ListInput from "@/components/list-input"
import SaveIcon from "@/components/save-icon"
import ListButton from "@/components/list-button"
import GetFilterPopulationsModal from "@/modals/get-filter-populations.modal"
import PagePlaceholder from "@/components/page-placeholder"
import ListContainer from "@/components/list-container"

interface StateProps extends WithTranslation {
  filter: FilterState
}

interface OwnProps {
  children?: ReactNode
}

const SHOW_DEFAULT_FILTERS: boolean = false

//Columns for filter array
const COLUMN_NAME_WIDTH: number = 220 //Width of name column
const COLUMN_POPULATIONS_WIDTH: number = 150 //Width of populatations column

//Modals
const MODAL_DELETE: string = "MODAL_DELETE" //Ask before delete filter
const MODAL_FILTER_POPULATION: string = "MODAL_FILTER_POPULATION"

function AdmFiltersWidget(props: StateProps & OwnProps) {
  const { t } = props

  //Current modal displayed
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Loading templates
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [currentFilter, setCurrentFilter] = useState<Filter | null>(null)

  //Load filters when page is loaded
  useEffect(() => {
    load()
    async function load() {
      setIsLoading(true)
      const filters: any = await store.dispatch(filterFetch(true, SHOW_DEFAULT_FILTERS))
      store.dispatch(filterGet(filters.error ? [] : filters))
      setIsLoading(false)
    }
  }, [])

  //Add filter
  function addFilter() {
    store.dispatch(filterAdd(new Filter({ name: "" })))
  }

  //Delete > open confirm dialog
  function deleteFilter(filter) {
    store.dispatch(filterActivate(filter))
    setCurrentModal(MODAL_DELETE)
  }

  //Confirm filter delete
  async function deleteFilterConfirm() {
    store.dispatch(filterStatus(STATUS_SAVING))
    const response: any = await store.dispatch(filterDestroy(props.filter.active.id))
    if (response.error) {
      store.dispatch(filterStatus(STATUS_SAVE_ERROR))
    } else {
      store.dispatch(filterStatus(STATUS_SAVED))
      store.dispatch(filterRemove(props.filter.active.id))
      setCurrentModal(null)
    }
  }

  //Edit filter name
  function editFilterName(value) {
    store.dispatch(filterEditAndUpdate("name", value))
  }

  return (
    <div>
      {
        /** dialog to confirm delete */
        currentModal === MODAL_DELETE && (
          <ModalConfirm
            onNo={() => setCurrentModal(null)}
            onYes={deleteFilterConfirm}
            status={props.filter.status}
            text={t("filter_ask_delete", {
              filter: props.filter.active.name
            })}
            textBold={t("utils_next_ask")}></ModalConfirm>
        )
      }

      {currentModal === MODAL_FILTER_POPULATION && (
        <GetFilterPopulationsModal
          filter={currentFilter ? currentFilter : undefined}
          onClose={() => setCurrentModal(null)}
        />
      )}

      <PageLoader status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
        {props.filter.list.length > 0 ? (
          <Card isWithoutPadding isFullWidth>
            <ListContainer
              title={t("filters_custom")}
              onAdd={() => addFilter()}
              onAddPrimary
              onAddTitle={t("filter_add_alt")}>
              {props.children && <div style={{ padding: "0 30px 20px" }}>{props.children}</div>}
              <ListFields>
                <ListCell width={COLUMN_NAME_WIDTH}>{t("filter_name_alt")}</ListCell>
                <ListCell width={COLUMN_POPULATIONS_WIDTH}>{t("attributes")}</ListCell>
                <Space />
              </ListFields>
              {props.filter.list
                .filter((x: Filter) => !x.default)
                .map((filter: Filter) => (
                  <ListItem key={filter.id}>
                    <ListCell width={COLUMN_NAME_WIDTH}>
                      <ListInput
                        isEdited={filter.id === props.filter.active.id}
                        onEdit={() => store.dispatch(filterActivate(filter))}
                        onEditEnd={editFilterName}
                        value={filter.name}
                      />
                    </ListCell>

                    <div className="flex1 grey-t flex flex-wrap" style={{ minWidth: COLUMN_POPULATIONS_WIDTH }}>
                      {filter.populations
                        .slice(0, 3)
                        .map((x) => x.name)
                        .join(", ")}
                      {filter.populations.length > 3 && <span>...</span>}
                    </div>

                    <ListCell>
                      <SaveIcon status={props.filter.active.id === filter.id ? props.filter.status : null} />
                    </ListCell>

                    <ListButton
                      text={t("show_populations")}
                      icon={faEye}
                      onClick={() => {
                        setCurrentFilter(filter)
                        setCurrentModal(MODAL_FILTER_POPULATION)
                      }}
                    />

                    <ListButton text={t("filter_delete")} icon={faTrash} onClick={() => deleteFilter(filter)} />
                  </ListItem>
                ))}
            </ListContainer>
          </Card>
        ) : (
          <Card>
            {props.children && (
              <>
                <h3>{t("filters_custom")}</h3>
                {props.children}
              </>
            )}
            <PagePlaceholder
              button={t("filter_add_alt")}
              image="sherlock"
              title={t("no_filter")}
              onClick={addFilter}
              help={t("filters_help")}
            />
          </Card>
        )}
      </PageLoader>
    </div>
  )
}

const mapStateToProps = (state) => ({
  filter: state.filter
})

export default connect(mapStateToProps)(withTranslation()(AdmFiltersWidget))
